// src/components/teacher/AddStudentModal.jsx
import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth-context';
import { useHydratedSupabaseClient } from '@/hooks/useHydratedSupabaseClient';

interface Classroom {
  id: string;
  name: string;
}

interface AddStudentModalProps {
  open: boolean;
  onClose?: () => void;
  onAdded?: (studentId: string | null) => void;
}

export default function AddStudentModal({ open, onClose, onAdded }: AddStudentModalProps) {
  const { session } = useAuth();
  const supabase = useHydratedSupabaseClient(session);
  const accessToken = session?.access_token || null;

  const [classes, setClasses] = useState<Classroom[]>([]);
  const [form, setForm] = useState({
    classroom_id: '',
    full_name: '',
    grade: '',
    guardian_name: '',
    guardian_email: '',
    guardian_phone: '',
    allergies: '',
    notes: '',
    iep: false,
    section504: false,
  });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');

  // Load teacher classrooms so they can choose where to add the student
  useEffect(() => {
    if (!open || !supabase) return;
    (async () => {
      try {
        setError('');
        // RLS allows teachers to see their own class; members can see as well per your policies
        const { data, error } = await supabase
          .from('classrooms')
          .select('id,name')
          .order('created_at', { ascending: false });
        if (error) throw error;
        setClasses(data || []);
        if (data?.length && !form.classroom_id) {
          setForm((f) => ({ ...f, classroom_id: data[0].id }));
        }
      } catch (e: unknown) {
        console.warn('[AddStudentModal] load classrooms failed', e);
        setClasses([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, supabase]);

  const close = () => {
    if (busy) return;
    setError('');
    setOkMsg('');
    onClose?.();
  };

  const submit = async (e: React.FormEvent) => {
    e?.preventDefault?.();
    if (!accessToken) return;
    if (!form.full_name.trim() || !form.classroom_id) {
      setError('Student name and classroom are required.');
      return;
    }

    setBusy(true);
    setError('');
    setOkMsg('');

    const healthFlags = (form.allergies || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const profile = {
      guardian_name: form.guardian_name || null,
      guardian_email: form.guardian_email || null,
      guardian_phone: form.guardian_phone || null,
      relationship: null,
      health_flags: healthFlags,
      notes: form.notes || null,
      flags: {
        iep: !!form.iep,
        section504: !!form.section504,
      },
    };

    const hasProfileData = Boolean(
      profile.guardian_name ||
        profile.guardian_email ||
        profile.guardian_phone ||
        profile.relationship ||
        (profile.notes && profile.notes.trim()) ||
        healthFlags.length ||
        profile.flags.iep ||
        profile.flags.section504
    );

    try {
      const payload = {
        classroom_id: form.classroom_id,
        student: {
          name: form.full_name.trim(),
          grade: form.grade || null,
        },
        profile: hasProfileData ? profile : undefined,
        guardian_invites: form.guardian_email
          ? [
              {
                email: form.guardian_email,
                relationship: null,
              },
            ]
          : [],
      };

      const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
      const res = await fetch(`${CORE_API}/api/v1/students`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || 'Unable to add student');
      setOkMsg('Student added.');
      onAdded?.(json.student?.id || null);
      // quick reset except the classroom
      setForm((f) => ({ ...f, full_name: '', grade: '', guardian_name: '', guardian_email: '', guardian_phone: '', allergies: '', notes: '', iep: false, section504: false }));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add student');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-cocoa">Add Student</h2>
          <button onClick={close} className="rounded px-2 py-1 text-sm text-cocoa/70 hover:bg-shell">Close</button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="text-xs text-cocoa/70">Classroom</label>
              <select
                className="w-full rounded border border-sand bg-white p-2 text-sm"
                value={form.classroom_id}
                onChange={(e) => setForm((f) => ({ ...f, classroom_id: e.target.value }))}
              >
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>{c.name || 'Classroom'}</option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-2">
              <label className="text-xs text-cocoa/70">Student Full Name *</label>
              <input
                className="w-full rounded border border-sand bg-white p-2 text-sm"
                value={form.full_name}
                onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                placeholder="e.g., Jordan Lee"
                required
              />
            </div>

            <div>
              <label className="text-xs text-cocoa/70">Grade</label>
              <input
                className="w-full rounded border border-sand bg-white p-2 text-sm"
                value={form.grade}
                onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))}
                placeholder="e.g., 3"
              />
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <label className="text-xs text-cocoa/70">Guardian Name</label>
              <input
                className="w-full rounded border border-sand bg-white p-2 text-sm"
                value={form.guardian_name}
                onChange={(e) => setForm((f) => ({ ...f, guardian_name: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs text-cocoa/70">Guardian Email</label>
              <input
                type="email"
                className="w-full rounded border border-sand bg-white p-2 text-sm"
                value={form.guardian_email}
                onChange={(e) => setForm((f) => ({ ...f, guardian_email: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs text-cocoa/70">Guardian Phone</label>
              <input
                className="w-full rounded border border-sand bg-white p-2 text-sm"
                value={form.guardian_phone}
                onChange={(e) => setForm((f) => ({ ...f, guardian_phone: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs text-cocoa/70">Allergies</label>
              <input
                className="w-full rounded border border-sand bg-white p-2 text-sm"
                value={form.allergies}
                onChange={(e) => setForm((f) => ({ ...f, allergies: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.iep} onChange={(e) => setForm((f) => ({ ...f, iep: e.target.checked }))} />
              IEP
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.section504} onChange={(e) => setForm((f) => ({ ...f, section504: e.target.checked }))} />
              Section 504
            </label>
          </div>

          <div>
            <label className="text-xs text-cocoa/70">Notes (private)</label>
            <textarea
              rows={3}
              className="w-full rounded border border-sand bg-white p-2 text-sm"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </div>

          {error ? <p className="text-sm text-brick">{error}</p> : null}
          {okMsg ? <p className="text-sm text-emerald-700">{okMsg}</p> : null}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={close} className="rounded border border-sand px-3 py-2 text-sm hover:bg-shell">Cancel</button>
            <button type="submit" disabled={busy} className="rounded bg-brick px-3 py-2 text-sm font-semibold text-white hover:bg-brickDark disabled:opacity-60">
              {busy ? 'Adding…' : 'Add Student'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
